import * as React from 'react';
import { scheduleUpdate } from 'app/infrastructure/platform/browser';

/**
 * The internal singleton registry to re-use one observer for multiple
 * observed targets
 */
export const targetRegistry = {};
export const observerRegistry = {};
// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
const removeInstance = (registry, name) => delete registry[name];
const getInstance = <T>(registry, name, initialValue: T) => {
  registry[name] = registry[name] || initialValue;
  return registry[name];
};

export interface ChildObserverProps{
  options?: Partial<IntersectionObserverInit>;
  name: string;
  active: boolean;
  id: number | string;
  onUpdate: (
    current:IntersectionObserverEntry[],
    nextUpdate: IntersectionObserverEntry[],
    previousUpdate: IntersectionObserverEntry[]
  ) => void;
  children: any;
}

/**
 * A component that can observe multiple children using one single observer.
 * Also supports children of type null becoming observed when rendered as component.
 * Each observed child target can provide it's own callback function to receive
 * updates when visibility tresholds are crossed.
 * This allows one callback to receive all observed children on every update.
 */
export class ChildObserver extends React.Component<ChildObserverProps>{

  private options: IntersectionObserverInit = {
    root: null,
    rootMargin: '-200px 0px',
    threshold: [.01, .05, .1, .2, .5, 1 ]
  };

  private targets: {id: number | string; callback: (...args:any) => void }[];
  private previousUpdate: IntersectionObserverEntry[];
  private observer:IntersectionObserver;

  constructor(props:ChildObserverProps){
    super(props);
    this.options = {...this.options, ...props.options};
  }

  public componentDidMount(){
    this.props.active &&
    this.props.children &&
    this.startObserving();
  }

  /**
   * Starts observing all queued children.
   *
   * By design, observe may be called multiple times for the same
   * dom element but still holding only one observer slot
   */
  private startObserving = () => {
    const {name, id, onUpdate} = this.props;
    this.observer = getInstance(
      observerRegistry,
      name,
      new IntersectionObserver(this.handleUpdatedIntersection, this.options)
    );
    this.targets = getInstance(targetRegistry, name, []);
    this.targets.push({id:id, callback: onUpdate});
    scheduleUpdate( ()=>
      this.targets.forEach(
        target => {
          const el =  document.getElementById(`${target.id}`);
          el && this.observer.observe(el);
        })
    );
  };

  public componentWillUnmount(){
   this.stopObserving();
  }

  private stopObserving = () => {
    const {name} = this.props;
    this.observer && this.observer.disconnect();
    removeInstance(observerRegistry, name);
    removeInstance(targetRegistry, name);
  };

  private restartObserving(){
    this.stopObserving();
    this.startObserving();
  }

  public componentDidUpdate(prev: ChildObserverProps){
    if( prev.active !== this.props.active ){
      this.props.active ? this.startObserving() : this.stopObserving();
    }
    if(prev.children === null && this.props.children){
      this.restartObserving();
    }
  }

  /**
   * When the amount of a target element which is visible within the root element crosses one of the visibility thresholds
   */
  public handleUpdatedIntersection = (updates:IntersectionObserverEntry[]) => {

    const nextUpdate = [
      ...(this.previousUpdate || updates)
    ];

    if(this.previousUpdate){
      updates.forEach(update => {
        const i = nextUpdate.findIndex(x => x.target.id === update.target.id);
        i > -1 && nextUpdate.splice(i, 1, update );
      });
    }

    this.targets.forEach(target => target.callback(updates, nextUpdate, this.previousUpdate) );
    this.previousUpdate = nextUpdate;
  };

  public render(){
    return this.props.children;
  }

}
